import { Link } from 'react-router';
import { motion } from 'motion/react';
import { Avatar, Badge, fadeInUp, useReducedMotion } from '@redpandev/ui';
import { avatarSources } from '../../lib/avatars';
import { ResendVerificationButton } from '../email/ResendVerificationButton';

export type ProfileHeroProps = {
  displayName: string;
  /** Id du catalogue d'avatars (spec 07) — null = mascotte Redpan. */
  avatarId: string | null;
  bio?: string | null;
  /** ISO 8601 — « Dans la meute depuis … ». */
  createdAt?: string;
  /** Infos privées : uniquement sur la page compte (jamais en public). */
  email?: string;
  emailVerified?: boolean;
  /** Changement d'email en attente de vérification (page compte uniquement). */
  pendingEmail?: string | null;
};

/** Anneau dégradé panda→soleil→bambou→ciel→raisin en rotation lente (off si reduced). */
function GradientRingAvatar({ avatarId, name }: { avatarId: string | null; name: string }) {
  const reduced = useReducedMotion();
  return (
    <span className="relative inline-flex h-24 w-24 shrink-0 items-center justify-center rounded-full">
      <motion.span
        aria-hidden="true"
        className="absolute inset-0 rounded-full bg-[conic-gradient(from_120deg,rgb(var(--panda-500)),rgb(var(--sun-400)),rgb(var(--bamboo-500)),rgb(var(--sky-400)),rgb(var(--grape-500)),rgb(var(--panda-500)))]"
        animate={reduced ? undefined : { rotate: 360 }}
        transition={reduced ? undefined : { repeat: Infinity, duration: 10, ease: 'linear' }}
      />
      <span aria-hidden="true" className="absolute inset-1 rounded-full bg-app" />
      <Avatar {...avatarSources(avatarId)} name={name} size={80} className="relative" />
    </span>
  );
}

/**
 * En-tête du « repaire » joueur : grand avatar à anneau animé, nom en display,
 * badges d'état. L'email n'apparaît que si fourni (profil privé).
 */
export function ProfileHero({
  displayName,
  avatarId,
  bio,
  createdAt,
  email,
  emailVerified,
  pendingEmail,
}: ProfileHeroProps) {
  const memberSince = createdAt
    ? new Date(createdAt).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    : null;

  return (
    <motion.section
      aria-label={`Profil de ${displayName}`}
      variants={fadeInUp}
      initial="hidden"
      animate="visible"
      className="flex flex-col items-center gap-4 text-center sm:flex-row sm:items-center sm:gap-6 sm:text-left"
    >
      <GradientRingAvatar avatarId={avatarId} name={displayName} />

      <div className="min-w-0">
        <h1 className="break-words font-display text-3xl font-bold text-ink-100 sm:text-4xl">
          {displayName}
        </h1>

        <div className="mt-2 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
          <Badge intent="brand">Membre de la meute</Badge>
          {email !== undefined &&
            (emailVerified ? (
              <Badge intent="success">✓ Email vérifié</Badge>
            ) : (
              <Badge intent="warning">Email non vérifié</Badge>
            ))}
          {/* Changement d'email en cours (spec 07 §3.3) — privé, jamais en
              public. Cliquable : c'est LE retour n°1 vers les réglages. */}
          {email !== undefined && pendingEmail && (
            <Link
              to="/settings/security?open=email"
              className="rounded-full"
              aria-label="Nouvel email en attente — gérer dans les paramètres"
            >
              <Badge intent="info">📬 Nouvel email en attente</Badge>
            </Link>
          )}
          {memberSince && <Badge intent="neutral">Depuis {memberSince}</Badge>}
        </div>

        {/* Renvoi de vérification : uniquement sur le compte privé, si non vérifié. */}
        {email !== undefined && emailVerified === false && (
          <ResendVerificationButton className="mt-3 flex flex-col items-center sm:items-start" />
        )}

        {email && <p className="mt-2 text-sm text-ink-400">{email}</p>}
        {bio && <p className="mt-3 max-w-prose text-ink-100/80">{bio}</p>}
      </div>
    </motion.section>
  );
}
